import { MeshTransmissionMaterial, useFBO } from "@react-three/drei";
import { createPortal, useFrame, useThree } from "@react-three/fiber";
import { easing } from "maath";
import React from "react";
import { useRef, useState } from "react";
import * as THREE from "three";

interface LensProps {
  damping?: number;
}

const Lens = ({
  children,
  damping = 0.14,
}: React.PropsWithChildren<LensProps>) => {
  const ref = useRef() as React.MutableRefObject<THREE.Mesh>;
  const buffer = useFBO();
  const viewport = useThree(state => state.viewport);
  const [scene] = useState(() => new THREE.Scene());

  useFrame((state, delta) => {
    // Tie lens to the pointer
    // getCurrentViewport gives us the width & height that would fill the screen in threejs units
    // By giving it a target coordinate we can offset these bounds, for instance width/height for a plane that
    // sits 15 units from 0/0/0 towards the camera (which is where the lens is)
    const viewport = state.viewport.getCurrentViewport(state.camera, [0, 0, 1]);

    easing.damp3(
      ref.current.position,
      [
        (state.pointer.x * viewport.width) / 2,
        (state.pointer.y * viewport.height) / 2,
        1,
      ],
      damping,
      delta
    );

    // This is entirely optional but spares us one extra render of the scene
    // The createPortal below will mount the children of <Lens> into the new THREE.Scene above
    // The following code will render that scene into a buffer, whose texture will then be fed into
    // a plane spanning the full screen and the lens transmission material
    state.gl.setRenderTarget(buffer);
    state.gl.render(scene, state.camera);
    state.gl.setRenderTarget(null);
  });

  return (
    <>
      {createPortal(children, scene)}
      <mesh scale={[viewport.width, viewport.height, 1]}>
        <planeGeometry />
        <meshBasicMaterial map={buffer.texture} />
      </mesh>
      <mesh scale={Math.min(viewport.width, viewport.height) * 0.14} ref={ref}>
        <circleGeometry args={[0.5, 32]} />
        <MeshTransmissionMaterial
          buffer={buffer.texture}
          anisotropy={0.5}
          anisotropicBlur={1.25}
          thickness={1.4}
          distortion={0.14}
          distortionScale={1.4}
          temporalDistortion={0.14}
          //   ior={1.14}
          //   chromaticAberration={0.05}
        />
      </mesh>
    </>
  );
};

export default Lens;
